import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  TrendingUp, Users, RefreshCw, MessageCircle, Target, Headphones,
  Shield, Clock, Award, Zap, Globe, BookOpen
} from 'lucide-react';

const Features = () => {
  const { t } = useTranslation();
  
  const featuresData = t('features.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
    icon: string;
  }>;

  const iconMap: Record<string, React.ElementType> = {
    TrendingUp,
    Users,
    RefreshCw,
    MessageCircle,
    Target,
    Headphones,
    Shield,
    Clock,
    Award,
    Zap,
    Globe,
    BookOpen
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="relative py-24 overflow-hidden bg-crypto-black">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] bg-crypto-red/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-crypto-accent/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('features.title')}
          </h2>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {featuresData.map((feature, index) => {
            const Icon = iconMap[feature.icon] || Target;
            
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -8, transition: { duration: 0.2 } }}
                className="group relative"
              >
                {/* Glow effect */}
                <div className="absolute -inset-0.5 bg-gradient-to-r from-crypto-red to-crypto-accent rounded-2xl opacity-0 group-hover:opacity-20 blur transition duration-500" />
                
                <div className="relative h-full bg-crypto-card border border-crypto-red/10 rounded-2xl p-8 hover:border-crypto-red/30 transition-all duration-300">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-gradient-to-br from-crypto-red/20 to-crypto-accent/20 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-crypto-red" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-crypto-red transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
